import React, { FC, useRef, useState } from 'react';
import { compact } from 'lodash';
import { Trans } from '@lingui/macro';
import { PropsWithClassName } from '@/types/common';
import Steps from '@/components/Common/Steps';
import Button, { ButtonVariant } from '@/components/Common/Button';
import ArrowIcon from '@/assets/icons/arrow.svg';
import {
  ConfigurableProduct,
  ConfigurableProductCharacteristic,
  ConfigurableProductCharacteristics,
  ConfiguredProductCharacteristic,
  ConfiguredProductCharacteristics,
  VariationView,
} from '@/types/index';
import { defaultCharacteristics, serializeConfiguredProduct } from '@/utils/configurator';
import DownloadButton from '@/components/Common/DownloadButton';
import ProductCharacteristicSelector from './ProductCharacteristicSelector';
import ConfiguredProductPreview from './ConfiguredProductPreview';
import * as styles from './ProductConfigurator.module.scss';

const isConfigurableCharacteristic = (c: ConfigurableProductCharacteristic) => c.title;

type Step = {
  characteristicIndex: number;
  title: string;
};

const getCharacteristicsAsSteps = (
  characteristics: ConfigurableProductCharacteristics
): Step[] =>
  compact(
    characteristics
      ?.map((characteristic, characteristicIndex) =>
        isConfigurableCharacteristic(characteristic)
          ? ({
              characteristicIndex,
              view: characteristic.view,
              title: characteristic.title,
            } as Step)
          : null
      )
      .filter(Boolean)
  );

type Props = PropsWithClassName<{
  product: ConfigurableProduct;
  characteristics: ConfigurableProductCharacteristics;
  defaultValue?: ConfiguredProductCharacteristics;
  onDone: (value: ConfiguredProductCharacteristics) => void;
}>;

const SingleConfigurator: FC<Props> = ({
  product,
  characteristics,
  defaultValue = defaultCharacteristics,
  onDone,
}) => {
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const steps = getCharacteristicsAsSteps(characteristics);
  const currentStep = steps[currentStepIndex];
  const configuration = useRef<HTMLDivElement>();

  const handleStepChange = (index: number) => {
    setCurrentStepIndex(index);
  };

  const handleSelectCharacteristic = (value: ConfiguredProductCharacteristic) => {
    setSelectedValue((prev) => {
      const updated = [...prev] as ConfiguredProductCharacteristics;
      if (currentStep?.characteristicIndex !== undefined) {
        updated[currentStep.characteristicIndex] = value;
      }
      return updated;
    });
  };

  const handleAddToEstimate = () => {
    onDone(selectedValue);
  };

  const isStepValid = !!selectedValue[currentStep?.characteristicIndex];
  const isDone = isStepValid && currentStepIndex === steps.length - 1;

  const sku = serializeConfiguredProduct({
    sku: product?.sku!,
    characteristics: selectedValue,
    quantity: 1,
  });

  const downloadUrl = `${process.env.GATSBY_API_URL}/preview?product=${sku}&download=true`;

  return (
    <div className={styles.main}>
      <Steps
        activeIndex={currentStepIndex}
        steps={steps.map((step, index) => ({
          title: step.title,
          isDisabled: !(!steps[index - 1] || !!selectedValue[currentStepIndex]),
        }))}
        onChange={handleStepChange}
      />

      <div
        ref={(node) => (configuration.current = node!)}
        className={styles.configuration}
      >
        <div className={styles.options}>
          <ProductCharacteristicSelector
            className={styles.samples}
            product={product}
            view={VariationView.ImageFinish}
            characteristicIndex={currentStep?.characteristicIndex}
            onChange={handleSelectCharacteristic}
            value={selectedValue[currentStep?.characteristicIndex] ?? null}
          />
        </div>

        <ConfiguredProductPreview
          className={styles.preview}
          product={{
            sku: product.sku,
            quantity: 1,
            characteristics: selectedValue,
          }}
        />
      </div>

      <div className={styles.navigation}>
        {
          <Button
            variant={ButtonVariant.Bare}
            onClick={() => setCurrentStepIndex(currentStepIndex - 1)}
            className={styles.previous}
            disabled={currentStepIndex === 0}
          >
            <ArrowIcon />
            <Trans>Back</Trans>
          </Button>
        }

        {currentStepIndex < steps.length - 1 ? (
          <Button
            variant={ButtonVariant.Bare}
            disabled={!isStepValid}
            onClick={() => setCurrentStepIndex(currentStepIndex + 1)}
            className={styles.next}
          >
            <Trans>Next step</Trans>
            <ArrowIcon />
          </Button>
        ) : isDone ? (
          <div className={styles.actions}>
            <DownloadButton
              className={styles.downloadImageButton}
              file={{
                filename: '',
                title: product.sku,
                url: downloadUrl,
              }}
              title={<Trans>Download image</Trans>}
            />
            <Button
              disabled={!isStepValid}
              variant={ButtonVariant.Primary}
              onClick={handleAddToEstimate}
              className={styles.previous}
            >
              <Trans>Add to estimate</Trans>
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SingleConfigurator;
